import React, { useState, useEffect, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import IntelShareCard from '../components/IntelShareCard';
import {
  Shield, RefreshCw, Calendar, FileText, Zap, TrendingUp,
  Download, Trophy, Lock, AlertTriangle, X, Eye
} from 'lucide-react';

// ── Leaderboard strip ──────────────────────────────────────────────────────────
const LeaderboardStrip = ({ board }) => {
  if (!board?.length) return null;
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="founders-leaderboard">
      <Trophy className="w-4 h-4 text-[#C5A52A] flex-shrink-0" />
      <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest flex-shrink-0">Reads</span>
      {board.map((entry, i) => (
        <div key={entry.username} className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[10px] font-mono text-gray-600">#{i + 1}</span>
          <span className="text-xs font-mono text-white">@{entry.username}</span>
          <span className={`text-xs font-bold font-mono ${i === 0 ? 'text-[#C5A52A]' : 'text-gray-400'}`}>
            {entry.total_clicks}
          </span>
          {i < board.length - 1 && <span className="text-gray-700 mx-1">·</span>}
        </div>
      ))}
    </div>
  );
};

// ── Share modal ────────────────────────────────────────────────────────────────
const ShareModal = ({ card, cardIndex, username, claimData, onClose }) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [templateIndex, setTemplateIndex] = useState(cardIndex % 3);
  const trackingUrl = claimData?.tracking_code
    ? `${window.location.origin}/api/r/${claimData.tracking_code}`
    : window.location.origin;

  const cleanQuestion = card.question.replace(/:\*\*/g, ':').replace(/\*\*/g, '').replace(/\*/g, '').trim();
  const HASHTAGS = '#CYQR #AccountabilityWorks #QueenCityQuestions';
  const shareText = `${cleanQuestion}\n\n📍 ${card.committee || 'City Council'}${card.meeting_date ? ` · ${card.meeting_date}` : ''}\n\n${HASHTAGS}\n\n🔗 ${trackingUrl}`;

  const uploadAndGetShareUrl = async () => {
    if (!previewUrl) return null;
    try {
      // Strip "data:image/png;base64," prefix
      const b64 = previewUrl.replace(/^data:image\/png;base64,/, '');
      const res = await api.post('/founders/intel/save-preview', {
        image_b64: b64,
        caption: shareText,
        question: cleanQuestion,
        tracking_code: claimData?.tracking_code || null,
      });
      return res.data.share_url;
    } catch (err) {
      console.error('Preview upload failed:', err);
      return null;
    }
  };

  const shareToFacebook = async () => {
    if (!previewUrl) { toast.error('Image still generating…'); return; }
    const shareUrl = await uploadAndGetShareUrl();
    const url = shareUrl || trackingUrl;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(cleanQuestion + '\n\n' + HASHTAGS)}`;
    window.open(fbUrl, '_blank', 'width=620,height=520,noopener');
  };

  const shareToX = async () => {
    if (!previewUrl) { toast.error('Image still generating…'); return; }
    const shareUrl = await uploadAndGetShareUrl();
    const url = shareUrl || trackingUrl;
    const text = `${cleanQuestion}\n\n${HASHTAGS}`;
    const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(xUrl, '_blank', 'width=620,height=420,noopener');
  };

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    setPreviewUrl(null);
    try {
      // Wait for template image to fully load before capturing
      const img = cardRef.current.querySelector('img');
      if (img && !img.complete) {
        await new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 1500);
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, 350));
      }
      const canvas = await html2canvas(cardRef.current, {
        scale: 1,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#000000',
      });
      setPreviewUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      toast.error('Image generation failed. Try a different template.');
    } finally {
      setGenerating(false);
    }
  }, [templateIndex]);  // eslint-disable-line

  useEffect(() => { generateImage(); }, [generateImage]);

  const handleShare = async () => {
    if (!previewUrl) {
      toast.error('Image still generating — wait a moment and try again.');
      return;
    }
    // On mobile: try native share sheet first
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile && navigator.canShare) {
      try {
        const blob = await fetch(previewUrl).then(r => r.blob());
        const file = new File([blob], 'cyqr-intel.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'CYQR Intel', text: shareText });
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // user cancelled — don't download
      }
    }
    // Desktop (or mobile fallback): download via Blob URL (works in all browsers)
    try {
      const blob = await fetch(previewUrl).then(r => r.blob());
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `cyqr-intel-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      toast.error('Download failed — try right-clicking the image and saving it.');
    }
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      toast.success('Caption copied!');
    } catch {
      toast.error('Copy failed — please select the text above and copy manually.');
    }
    document.body.removeChild(ta);
  };

  const copyCaption = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareText)
        .then(() => toast.success('Caption copied!'))
        .catch(() => fallbackCopy(shareText));
    } else {
      fallbackCopy(shareText);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-t-2xl sm:rounded-lg w-full sm:max-w-2xl max-h-[93dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1a1a1a]">
          <h3 className="text-white font-bold font-mono tracking-wider text-sm">YOUR INTEL CARD</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Template picker */}
        <div className="px-4 pt-4 flex items-center gap-3">
          <span className="text-xs text-gray-500 font-mono uppercase tracking-wide">Template:</span>
          {[0, 1, 2].map(i => (
            <button
              key={`template-${i}`}
              onClick={() => setTemplateIndex(i)}
              data-testid={`template-${i}`}
              className={`w-9 h-9 rounded border-2 transition-all ${
                templateIndex === i ? 'border-[#C5A52A] scale-110' : 'border-[#2a2a2a] hover:border-gray-500'
              }`}
              style={{ background: ['#2a2a2a', '#7a0000', '#C5A52A'][i] }}
            />
          ))}
        </div>

        {/* Hidden full-size card for capture — off-screen, key forces remount on template change */}
        <div key={templateIndex} style={{ position: 'fixed', left: '-9999px', top: 0, pointerEvents: 'none' }}>
          <IntelShareCard
            ref={cardRef}
            question={cleanQuestion}
            username={username}
            committee={card.committee}
            meetingDate={card.meeting_date}
            templateIndex={templateIndex}
          />
        </div>

        {/* Preview */}
        <div className="px-4 pb-4">
          <div className="bg-[#111] border border-[#1a1a1a] rounded overflow-hidden flex items-center justify-center" style={{ minHeight: 300 }}>
            {generating && (
              <div className="flex flex-col items-center gap-2 py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#C5A52A]" />
                <p className="text-xs text-gray-500 font-mono">Generating image…</p>
              </div>
            )}
            {!generating && previewUrl && (
              <img src={previewUrl} alt="Intel card preview" className="max-w-full rounded" style={{ maxHeight: 400 }} />
            )}
          </div>

          {/* Share text block */}
          <div className="mt-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded p-3">
            <p className="text-[10px] text-gray-500 font-mono uppercase tracking-wide mb-2">Copy this as your post caption</p>
            <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{shareText}</p>
          </div>

          {/* Tracking notice */}
          <div className="mt-3 flex items-start gap-2 bg-[#C5A52A]/10 border border-[#C5A52A]/30 rounded p-3">
            <Eye className="w-3.5 h-3.5 text-[#C5A52A] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-400">
              Your tracking link is embedded in the caption. Every time someone clicks it, your read count goes up on the briefing page.
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 mt-4 pb-4">
            {/* Primary social share row */}
            <div className="flex gap-2">
              <button
                onClick={shareToFacebook}
                disabled={!previewUrl || generating}
                data-testid="share-facebook-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1877F2] text-white font-bold rounded text-sm hover:bg-[#1565c0] disabled:opacity-40 transition-colors"
              >
                SHARE TO FACEBOOK
              </button>
              <button
                onClick={shareToX}
                disabled={!previewUrl || generating}
                data-testid="share-x-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-black text-white font-bold rounded text-sm hover:bg-[#111] border border-[#333] disabled:opacity-40 transition-colors"
              >
                SHARE TO X
              </button>
            </div>
            {/* Note */}
            <p className="text-[10px] text-gray-600 text-center font-mono">
              Facebook and X will show the card image as the post preview.
            </p>
            {/* Secondary: manual download + copy caption */}
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                disabled={!previewUrl || generating}
                data-testid="share-card-btn"
                className="flex-1 py-2.5 bg-[#1a1a1a] text-gray-400 font-mono text-xs rounded hover:bg-[#2a2a2a] border border-[#2a2a2a] transition-colors"
              >
                DOWNLOAD IMAGE
              </button>
              <button
                onClick={copyCaption}
                data-testid="copy-caption-btn"
                className="flex-1 py-2.5 bg-[#1a1a1a] text-gray-400 font-mono text-xs rounded hover:bg-[#2a2a2a] border border-[#2a2a2a] transition-colors"
              >
                COPY CAPTION
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Single intel card ──────────────────────────────────────────────────────────
const IntelCard = ({ card, index, claimStatus, onClaim }) => {
  const claimKey = `${card.meeting_id}_${index}`;
  const claim = claimStatus[claimKey];
  const isClaimed = !!claim;
  const isMine = claim?.is_mine;
  const clickCount = claim?.click_count ?? 0;
  const [claiming, setClaiming] = useState(false);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await onClaim(card, index);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden border transition-all duration-300 ${
        isClaimed && !isMine
          ? 'border-[#1a1a1a] opacity-60'
          : 'border-[#1a1a1a] hover:border-[#C5A52A]/40 bg-[#0d0d0d]'
      }`}
      data-testid={`intel-card-${index}`}
    >
      {/* Gold top line */}
      <div className="h-[2px] bg-gradient-to-r from-[#C5A52A] via-[#D4B93A] to-transparent" />

      <div className="p-5">
        {/* Meta */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[#C5A52A] text-[9px] font-mono tracking-[0.2em] uppercase bg-[#C5A52A]/10 px-2 py-0.5 rounded flex items-center gap-1">
            <FileText className="w-2.5 h-2.5" />{card.committee || 'City Council'}
          </span>
          {card.meeting_date && (
            <span className="text-gray-600 text-[9px] font-mono flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" />{card.meeting_date}
            </span>
          )}
          <span className="ml-auto text-gray-800 text-[9px] font-mono">#{String(index + 1).padStart(2, '0')}</span>
        </div>

        {/* Question — strip markdown artifacts */}
        <p className={`text-sm leading-relaxed font-medium mb-4 ${isClaimed && !isMine ? 'text-gray-500' : 'text-white'}`}>
          {card.question.replace(/:\*\*/g, ':').replace(/\*\*/g, '').replace(/\*/g, '').replace(/^["']|["']$/g, '').trim()}
        </p>

        {/* Action row */}
        <div className="border-t border-[#1a1a1a] pt-3">
          {!isClaimed && (
            <button
              onClick={handleClaim}
              disabled={claiming}
              data-testid={`claim-btn-${index}`}
              className="w-full py-2.5 bg-[#C5A52A] text-black font-bold text-sm rounded hover:bg-yellow-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {claiming ? (
                <><div className="w-3.5 h-3.5 border-t-2 border-black rounded-full animate-spin" /> CLAIMING…</>
              ) : (
                <><Shield className="w-3.5 h-3.5" /> CLAIM & SHARE</>
              )}
            </button>
          )}

          {isMine && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-[#C5A52A]/10 border border-[#C5A52A]/30 rounded px-3 py-2 flex-1">
                <Eye className="w-4 h-4 text-[#C5A52A]" />
                <span className="text-[#C5A52A] font-bold text-lg font-mono">{clickCount}</span>
                <span className="text-gray-500 text-xs">reads</span>
              </div>
              <button
                onClick={handleClaim}
                disabled={claiming}
                data-testid={`redownload-btn-${index}`}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#1a1a1a] text-gray-300 text-xs font-mono rounded border border-[#2a2a2a] hover:border-[#C5A52A]/40 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> RE-DOWNLOAD
              </button>
            </div>
          )}

          {isClaimed && !isMine && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-xs text-gray-600 font-mono uppercase tracking-wide">Claimed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-gray-400 font-bold font-mono text-sm">{clickCount}</span>
                <span className="text-gray-600 text-xs">reads</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Skeleton ───────────────────────────────────────────────────────────────────
const CardSkeleton = () => (
  <div className="animate-pulse bg-[#0d0d0d] border border-[#1f1f1f] rounded-lg p-5 space-y-3">
    <div className="h-3 w-1/3 bg-[#1a1a1a] rounded" />
    <div className="h-5 w-full bg-[#1a1a1a] rounded" />
    <div className="h-5 w-4/5 bg-[#1a1a1a] rounded" />
  </div>
);

// ── Main IntelligenceBriefing ──────────────────────────────────────────────────
const IntelligenceBriefing = () => {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [claimStatus, setClaimStatus] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareModal, setShareModal] = useState(null); // { card, index, claimData }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [briefRes, claimRes, boardRes] = await Promise.all([
        api.get('/founders/intelligence-briefing'),
        api.get('/founders/intel/claim-status').catch(() => ({ data: { claims: {} } })),
        api.get('/founders/intel/leaderboard').catch(() => ({ data: { leaderboard: [] } })),
      ]);
      setCards(briefRes.data.cards || []);
      setStats(briefRes.data.stats || null);
      setClaimStatus(claimRes.data.claims || {});
      setLeaderboard(boardRes.data.leaderboard || []);
    } catch (err) {
      setError(err.response?.status === 403 ? 'founders_only' : 'failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleClaim = async (card, index) => {
    try {
      const res = await api.post('/founders/intel/claim', {
        question_text: card.question,
        meeting_id: card.meeting_id,
        question_index: index,
        committee: card.committee,
        meeting_date: card.meeting_date,
      });
      // Refresh claim status
      const claimRes = await api.get('/founders/intel/claim-status');
      setClaimStatus(claimRes.data.claims || {});
      // Open share modal
      setShareModal({ card, index, claimData: res.data });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Already claimed by another founder.');
      } else {
        toast.error(err.response?.data?.detail || 'Claim failed');
      }
    }
  };

  const username = user?.username || user?.email?.split('@')[0] || 'founder';

  return (
    <div className="space-y-6" data-testid="intelligence-briefing">

      {shareModal && (
        <ShareModal
          card={shareModal.card}
          cardIndex={shareModal.index}
          username={username}
          claimData={shareModal.claimData}
          onClose={() => { setShareModal(null); fetchAll(); }}
        />
      )}

      {/* Section header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-[#C5A52A]" />
            <span className="text-[#C5A52A] font-mono text-[10px] tracking-[0.3em] uppercase">Founders Intelligence Briefing</span>
            <span className="bg-red-900/30 border border-red-800/50 text-red-400 font-mono text-[8px] tracking-widest px-1.5 py-0.5 rounded">RESTRICTED</span>
          </div>
          <p className="text-gray-500 text-xs leading-relaxed max-w-lg">
            AI-surfaced questions from audited Regina council meetings.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white font-mono px-3 py-1.5 bg-[#111] border border-[#2a2a2a] rounded hover:border-[#C5A52A]/40 transition-colors"
          data-testid="refresh-briefing"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Exclusivity banner */}
      <div className="bg-[#C5A52A]/10 border border-[#C5A52A]/40 rounded-lg p-4" data-testid="exclusivity-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-[#C5A52A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-[#C5A52A] mb-1">These cards are exclusive — first founder to claim it owns it.</p>
            <p className="text-xs text-gray-400 leading-relaxed">
              Once you click <strong className="text-white">CLAIM & SHARE</strong>, that question is locked to you and grays out for everyone else.
              Your share includes a tracking link — every time someone clicks it, your read count goes up.
              Don't claim it unless you're ready to post it.
            </p>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg px-4 py-3">
          <LeaderboardStrip board={leaderboard} />
        </div>
      )}

      {/* Stats strip */}
      {stats && !loading && (
        <div className="flex items-center gap-6 text-[10px] font-mono text-gray-600 border border-[#1a1a1a] bg-[#0a0a0a] rounded px-4 py-2 flex-wrap">
          <span className="flex items-center gap-1.5"><FileText className="w-3 h-3 text-[#C5A52A]" />{stats.meetings_audited} meetings audited</span>
          <span className="text-[#2a2a2a]">·</span>
          <span className="flex items-center gap-1.5"><TrendingUp className="w-3 h-3 text-orange-500" />{stats.total_flags} flags raised</span>
          <span className="text-[#2a2a2a]">·</span>
          <span className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-yellow-500" />{stats.questions_available} questions</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <CardSkeleton key={`skeleton-${i}`} />)}
        </div>
      )}

      {/* Empty */}
      {!loading && cards.length === 0 && !error && (
        <div className="text-center py-16 border border-dashed border-[#2a2a2a] rounded-lg">
          <Shield className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-mono">No briefing data yet.</p>
          <p className="text-gray-700 text-xs mt-1">Admin needs to run a Council Audit first.</p>
        </div>
      )}

      {/* Error */}
      {!loading && error === 'failed' && (
        <div className="text-center py-12 text-gray-600 text-sm">Failed to load briefing. Try refreshing.</div>
      )}

      {/* Cards grid */}
      {!loading && cards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="briefing-cards">
          {cards.map((card, i) => (
            <IntelCard
              key={`${card.meeting_id}-${i}`}
              card={card}
              index={i}
              claimStatus={claimStatus}
              onClaim={handleClaim}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default IntelligenceBriefing;
